import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@app/app.hook'
import { selectActiveModules, selectStrings } from '@app/slices/slice.app'

import { MODULE_TABLE } from '@app/app.config'
import {
    ACTION_MUTATION_PROMISE,
    TOASTIFY_DEFAULT_OPTIONS,
    UNLINKED_LIMIT
} from '@app/app.constants'
import { getErrorText } from '@app/app.method'
import { selectToken } from '@app/slices/slice.token'
import { useInitializeSidebarVisibility } from '@login/MutationProvider/initializeSidebarVisibility'
import { useRevalidateToken } from '@login/MutationProvider/revalidateToken'
import { useValidateAPIPath } from '@login/MutationProvider/validateAPIPath'
import { useValidateRoute } from '@login/MutationProvider/validateRoute'

import {
    DEPARTMENT_FORMIK_INITIAL_VALUES,
    DEPARTMENT_VALIDATION_SCHEMA,
    FACILITY_STEP_FOUR_FORMIK_INITIAL_VALUES,
    FACILITY_STEP_THREE_FORMIK_INITIAL_VALUES,
    MODULE_VERSION
} from '@fmt/constants'
import {
    CareProfessional,
    DepartmentKeys,
    DepartmentParams,
    GetCareProfessionalsResponse,
    GetDepartmentResponse,
    GetTreatmentsResponse,
    Treatment,
    UpdateProfilePictureDepartmentResponse
} from '@fmt/type'
import { ImageDisplay } from '@profile/styles/ImageDisplay'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'

import { selectRouter } from '@app/app.store'
import { TokenData } from '@app/types/type.token'
import {
    useAddDepartmentCareProfessionalMutation,
    useAddDepartmentTreatmentMutation,
    useDeleteDepartmentCareProfessionalMutation,
    useDeleteDepartmentTreatmentMutation,
    useEditDepartmentMutation,
    useGetCareProfessionalsMutation,
    useGetDepartmentMutation,
    useGetTreatmentsMutation,
    useUpdateProfilePictureDepartmentMutation
} from '@fmt/api'
import DashboardResponsiveHeader from '@fmt/components/DashboardResponsiveHeader'
import CareProfessionalsViewTable from '@fmt/components/departments/details/CareProfessionalsTable'
import TreatmentsTable from '@fmt/components/departments/details/TreatmentsTable'
import Address from '@fmt/components/departments/details/inputs/Address'
import City from '@fmt/components/departments/details/inputs/City'
import DepartmentName from '@fmt/components/departments/details/inputs/DepartmentName'
import Email from '@fmt/components/departments/details/inputs/Email'
import GeneralPhoneNumber from '@fmt/components/departments/details/inputs/GeneralPhoneNumber'
import PostalCode from '@fmt/components/departments/details/inputs/PostalCode'
import StreetName from '@fmt/components/departments/details/inputs/StreetName'
import Website from '@fmt/components/departments/details/inputs/Website'
import { back, push } from '@lagunovsky/redux-react-router'

import _ from 'lodash'
import { useParams } from 'react-router-dom'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'

// eslint-disable-next-line max-len
import CareProfessionalsInterface from '@fmt/components/departments/details/CareprofessionalsInterface'
import TreatmentsInterface from '@fmt/components/departments/details/TreatmentsInterface'

const ProfilePic = ({
    showRecentlyUploaded,
    updateProfilePictureDepartmentMutation,
    getDepartmentMutation
}: {
    showRecentlyUploaded: boolean
    updateProfilePictureDepartmentMutation: {
        data: UpdateProfilePictureDepartmentResponse | undefined
    }
    getDepartmentMutation: {
        data?: GetDepartmentResponse | undefined
    }
}) => {
    const url = showRecentlyUploaded
        ? updateProfilePictureDepartmentMutation.data?.data.url
        : getDepartmentMutation.data?.data.profilePicture
    const imageThumbnail = url
        ? <ImageDisplay
            url={url}
            className={'rounded-square profile'}
        />
        : <div
            className={[
                'img-placeholder rounded-square profile'
            ].join(' ')}
        >
        </div>

    return imageThumbnail
}

const DepartmentDetailsInterface = () => {
    const dispatch = useAppDispatch()
    const strings = useAppSelector(selectStrings)

    const activeModules = useAppSelector(selectActiveModules)

    const revalidateToken = useRevalidateToken()
    const token = useAppSelector(selectToken)
    const validateAPIPath = useValidateAPIPath()
    const router = useAppSelector(selectRouter)
    const validateRoute = useValidateRoute()
    const initializeSidebarVisibility = useInitializeSidebarVisibility()

    // upload profile picture logic.
    const [file, setFile] = useState<File | null>(null)
    const [getDepartment, getDepartmentMutation] = useGetDepartmentMutation()

    const [getUnlinkedCareprofessionals,
        getUnlinkedCareprofessionalsMutation
    ] = useGetCareProfessionalsMutation()

    const [getLinkedCareprofessionals,
        getLinkedCareprofessionalsMutation
    ] = useGetCareProfessionalsMutation()

    const [getUnlinkedTreatments,
        getUnlinkedTreatmentsMutation
    ] = useGetTreatmentsMutation()

    const [getLinkedTreatments,
        getLinkedTreatmentsMutation
    ] = useGetTreatmentsMutation()
    const [searchLinkedCareprofs, setSearchLinkedCareprofs] = useState<string>('')
    const [searchLinkedTreatments, setSearchLinkedTreatments] = useState<string>('')

    const [currentPageCareprofessionals, setCurrentPageCareprofessionals] = useState(0)
    const [currentPageTreatments, setCurrentPageTreatments] = useState(0)

    const [showRecentlyUploaded, setShowRecentlyUploaded] = useState<boolean>(false)
    const [showCancelWarning, setShowCancelWarning] = useState<boolean>(false)

    const { departmentId } = useParams<DepartmentParams>()

    const [editDepartment, editDepartmentMutation] = useEditDepartmentMutation()

    const [
        addDepartmentCareprofessional,
        addDepartmentCareprofessionalMutation
    ] = useAddDepartmentCareProfessionalMutation({})

    const [
        deleteDepartmentCareProfessional,
        deleteDepartmentCareProfessionalMutation
    ] = useDeleteDepartmentCareProfessionalMutation({})

    const [
        addDepartmentTreatment,
        addDepartmentTreatmentMutation
    ] = useAddDepartmentTreatmentMutation({})

    const [
        deleteDepartmentTreatment,
        deleteDepartmentTreatmentMutation
    ] = useDeleteDepartmentTreatmentMutation({})

    const [mode, setMode] = useState<'EDIT' | 'VIEW'>(
        _.lowerCase(router.location.pathname).includes('edit') ? 'EDIT' : 'VIEW'
    )

    const [searchUnlinkedCareprof, setSearchUnlinkedCareprof] = useState('')
    const [currentPageUnlinkedCareprof, setCurrentPageUnlinkedCareprof] = useState(0)

    const [searchTreatment, setSearchTreatment] = useState('')
    const [currentPageTreatment, setCurrentPageTreatment] = useState(0)

    // a fix that does the ff things:
    // before this existed, the getPatients gets called twice from two separate useEffects
    // the first hook should only be done once you clicked on either buttons that
    // changes the url. ideally, a quick solution. should be set to FALSE.
    const [modeButtonClicked, setModeButtonClicked] = useState(false)

    useEffect(() => {
        initializeSidebarVisibility(true)
    }, [])

    // a workaround to prevent tables from flickering. simple but it works.
    const [
        getUnlinkedCareprofessionalsResponse,
        setGetUnlinkedCareprofessionalsResponse
    ] = useState<GetCareProfessionalsResponse>()

    const [
        getLinkedCareprofessionalsResponse,
        setGetLinkedCareprofessionalsResponse
    ] = useState<GetCareProfessionalsResponse>()

    const [
        getUnlinkedTreatmentsResponse,
        setGetUnlinkedTreatmentsResponse
    ] = useState<GetTreatmentsResponse>()

    const [
        getLinkedTreatmentsResponse,
        setGetLinkedTreatmentsResponse
    ] = useState<GetTreatmentsResponse>()

    const LinkCareprofessionalsFormik = useFormik({
        initialValues: FACILITY_STEP_THREE_FORMIK_INITIAL_VALUES,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {
        }
    })

    const LinkTreatmentsFormik = useFormik({
        initialValues: FACILITY_STEP_FOUR_FORMIK_INITIAL_VALUES,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {
        }
    })

    useEffect(() => {
        if (getLinkedCareprofessionalsResponse) {
            LinkCareprofessionalsFormik.setFieldValue('linkedCareprofessionals',
                [{
                    department: getDepartmentMutation.data?.data,
                    collapsible: true,
                    arr: getLinkedCareprofessionalsResponse.data.careprofessionals
                }]
            )
        }
    }, [getLinkedCareprofessionalsResponse])

    useEffect(() => {
        if (getLinkedTreatmentsResponse) {
            LinkTreatmentsFormik.setFieldValue('linkedTreatments',
                [{
                    department: getDepartmentMutation.data?.data,
                    collapsible: true,
                    arr: getLinkedTreatmentsResponse.data.treatments
                }]
            )
        }
    }, [getLinkedTreatmentsResponse])

    useEffect(() => {
        if (getUnlinkedCareprofessionalsMutation.data) {
            setGetUnlinkedCareprofessionalsResponse(getUnlinkedCareprofessionalsMutation
                .data)
        }
    }, [getUnlinkedCareprofessionalsMutation.data])

    useEffect(() => {
        if (getLinkedCareprofessionalsMutation.data) {
            setGetLinkedCareprofessionalsResponse(getLinkedCareprofessionalsMutation.data)
        }
    }, [getLinkedCareprofessionalsMutation.data])

    useEffect(() => {
        if (getUnlinkedTreatmentsMutation.data) {
            setGetUnlinkedTreatmentsResponse(getUnlinkedTreatmentsMutation
                .data)
        }
    }, [getUnlinkedTreatmentsMutation.data])

    useEffect(() => {
        if (getLinkedTreatmentsMutation.data) {
            setGetLinkedTreatmentsResponse(getLinkedTreatmentsMutation.data)
        }
    }, [getLinkedTreatmentsMutation.data])

    useEffect(() => {
        if (addDepartmentTreatmentMutation.error) {
            const message = getErrorText(addDepartmentTreatmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [addDepartmentTreatmentMutation.error])

    useEffect(() => {
        if (deleteDepartmentTreatmentMutation.error) {
            const message = getErrorText(deleteDepartmentTreatmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [deleteDepartmentTreatmentMutation.error])

    useEffect(() => {
        if (addDepartmentCareprofessionalMutation.error) {
            const message = getErrorText(addDepartmentCareprofessionalMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [addDepartmentCareprofessionalMutation.error])

    useEffect(() => {
        if (deleteDepartmentCareProfessionalMutation.error) {
            const message = getErrorText(deleteDepartmentCareProfessionalMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [deleteDepartmentCareProfessionalMutation.error])

    const unsubscribeGetUnlinkedCareprofessionals = () => {
        const unsubscribeMutation = getUnlinkedCareprofessionals({
            data: {}
        } as any)
        unsubscribeMutation.abort()
        unsubscribeMutation.unsubscribe()
    }

    const unsubscribeGetLinkedCareprofessionals = () => {
        const unsubscribeMutation = getLinkedCareprofessionals({
            data: {}
        } as any)
        unsubscribeMutation.abort()
        unsubscribeMutation.unsubscribe()
    }

    const unsubscribeGetUnlinkedTreatments = () => {
        const unsubscribeMutation = getUnlinkedTreatments({
            data: {}
        } as any)
        unsubscribeMutation.abort()
        unsubscribeMutation.unsubscribe()
    }

    const unsubscribeGetLinkedTreatments = () => {
        const unsubscribeMutation = getLinkedTreatments({
            data: {}
        } as any)
        unsubscribeMutation.abort()
        unsubscribeMutation.unsubscribe()
    }

    const departmentEditFormik = useFormik({
        initialValues: getDepartmentMutation.data?.data || DEPARTMENT_FORMIK_INITIAL_VALUES,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: DEPARTMENT_VALIDATION_SCHEMA(
            strings.app?.message.error.empty || ''
        ),
        onSubmit: (values) => {
            const call = async () => {
                if (token.valid) {
                    const newToken = await revalidateToken(
                        {
                            value: token.value,
                            id: token.id
                        },
                        token.mode
                    )
                    const foundApiPath = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths.editDepartment.path,
                        true
                    )

                    if (foundApiPath && newToken.value) {
                        editDepartment({
                            authToken: newToken.value,
                            data: {
                                departmentId: departmentId || '',
                                departmentName: values.departmentName || '',
                                departmentAddressStreet: values.departmentAddressStreet,
                                departmentAddressNumber: values.departmentAddressNumber,
                                departmentAddressSuffix: values.departmentAddressSuffix || '',
                                departmentPostalcode: values.departmentPostalcode,
                                departmentCity: values.departmentCity,
                                departmentPhonenumber: values.departmentPhonenumber || '',
                                departmentEmailAddress: values.departmentEmailAddress || '',
                                departmentWebsite: values.departmentWebsite || ''
                            }
                        })
                    }
                }
            }
            call()
        }
    })

    /** create fetch data function */
    const fetchData = (token: TokenData) => {
        let isMounted = true

        const call = async () => {
            if (
                token.valid &&
                departmentId
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getDepartment.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        getDepartment({
                            authToken: newToken.value,
                            data: {
                                departmentId
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
        }
    }

    const fetchUnlinkedCareprofessionals = (token: TokenData) => {
        unsubscribeGetUnlinkedCareprofessionals()

        let getUnlinkedCareprofessionalsPromise = _.cloneDeep(ACTION_MUTATION_PROMISE)
        let isMounted = true

        const call = async () => {
            if (
                token.valid
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getCareProfessionals.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        const skip = currentPageUnlinkedCareprof *
                         (getUnlinkedCareprofessionalsResponse
                             ?.data.limit || 1)

                        const totalRecords = (getUnlinkedCareprofessionalsResponse
                            ?.data.totalRecords || 0) - 1

                        getUnlinkedCareprofessionalsPromise = getUnlinkedCareprofessionals({
                            authToken: newToken.value,
                            data: {
                                departmentId,
                                isLinked: false,
                                limit: UNLINKED_LIMIT,
                                search: searchUnlinkedCareprof,
                                skip: skip > (getUnlinkedCareprofessionalsResponse?.data
                                    .totalRecords || 0)
                                    ? totalRecords
                                    : skip
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
            getUnlinkedCareprofessionalsPromise && getUnlinkedCareprofessionalsPromise.abort()
        }
    }

    const fetchLinkedCareprofessionals = (token: TokenData) => {
        unsubscribeGetLinkedCareprofessionals()

        let getLinkedCareprofessionalsPromise = _.cloneDeep(ACTION_MUTATION_PROMISE)

        let isMounted = true

        const call = async () => {
            if (
                token.valid
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getCareProfessionals.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        const skip = currentPageCareprofessionals *
                        (getLinkedCareprofessionalsResponse
                            ?.data.limit || 1)

                        const totalRecords = (getLinkedCareprofessionalsResponse
                            ?.data.totalRecords || 0) - 1

                        getLinkedCareprofessionalsPromise = getLinkedCareprofessionals({
                            authToken: newToken.value,
                            data: {
                                departmentId,
                                isLinked: true,
                                search: searchLinkedCareprofs,
                                skip: skip > (getLinkedCareprofessionalsResponse?.data
                                    .totalRecords || 0)
                                    ? totalRecords
                                    : skip
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
            getLinkedCareprofessionalsPromise && getLinkedCareprofessionalsPromise.abort()
        }
    }

    const fetchDataUnlinkedTreatments = (token: TokenData) => {
        unsubscribeGetUnlinkedTreatments()

        let getUnlinkedTreatmentsPromise = _.cloneDeep(ACTION_MUTATION_PROMISE)

        let isMounted = true

        const call = async () => {
            if (
                token.valid
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getTreatments.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        const skip = currentPageTreatment * (getUnlinkedTreatmentsResponse
                            ?.data.limit || 1)

                        const totalRecords = (getUnlinkedTreatmentsResponse
                            ?.data.totalRecords || 0) - 1

                        getUnlinkedTreatmentsPromise = getUnlinkedTreatments({
                            authToken: newToken.value,
                            data: {
                                departmentId,
                                isLinked: false,
                                limit: UNLINKED_LIMIT,
                                skip: skip > (getUnlinkedTreatmentsResponse?.data
                                    .totalRecords || 0)
                                    ? totalRecords
                                    : skip
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
            getUnlinkedTreatmentsPromise && getUnlinkedTreatmentsPromise.abort()
        }
    }

    const fetchDataLinkedTreatments = (token: TokenData) => {
        unsubscribeGetLinkedTreatments()

        let getLinkedTreatmentsPromise = _.cloneDeep(ACTION_MUTATION_PROMISE)

        let isMounted = true

        const call = async () => {
            if (
                token.valid
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getTreatments.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        const skip = currentPageTreatments *
                        (getLinkedTreatmentsResponse
                            ?.data.limit || 1)

                        const totalRecords = (getLinkedTreatmentsResponse
                            ?.data.totalRecords || 0) - 1

                        getLinkedTreatmentsPromise = getLinkedTreatments({
                            authToken: newToken.value,
                            data: {
                                departmentId,
                                isLinked: true,
                                skip: skip > (getLinkedCareprofessionalsResponse?.data
                                    .totalRecords || 0)
                                    ? totalRecords
                                    : skip
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
            getLinkedTreatmentsPromise && getLinkedTreatmentsPromise.abort()
        }
    }

    // THIS FETCH SHOULD NOT HAPPEN ON MOUNT.
    // solution: change dependency to a useState. a button where you clicked on it
    // and then unset it.
    useEffect(() => {
        // should only be performed IF the button is clicked.
        if (modeButtonClicked) {
            setMode(
                _.lowerCase(router.location.pathname).includes('edit') ? 'EDIT' : 'VIEW'
            )

            // you go here ideally after submitting the values from the edit mode.
            if (_.lowerCase(router.location.pathname).includes('view')) {
                departmentEditFormik.resetForm()
                // and then fetch the data again just to double check.
                // you only want to do this IF you came from clicking the button.
                fetchData(token)
            }

            // don't forget to set it false after.
            setModeButtonClicked(false)
        }
    }, [router.location.pathname, modeButtonClicked])

    // ONLY fetch it when needAccordion is true AND if isSuccess is false.
    useEffect(() => {
        if (departmentId) {
            setShowRecentlyUploaded(false)
            return fetchData(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, departmentId])

    useEffect(() => {
        if (departmentId) {
            return fetchUnlinkedCareprofessionals(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, departmentId,
        currentPageUnlinkedCareprof, searchUnlinkedCareprof])

    useEffect(() => {
        if (departmentId) {
            return fetchLinkedCareprofessionals(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, departmentId,
        currentPageCareprofessionals, searchLinkedCareprofs])

    useEffect(() => {
        if (departmentId) {
            return fetchDataUnlinkedTreatments(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, departmentId,
        currentPageTreatment
    ])

    useEffect(() => {
        if (departmentId) {
            return fetchDataLinkedTreatments(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, departmentId,
        currentPageTreatments, searchLinkedTreatments])

    useEffect(() => {
        if (getDepartmentMutation.data) {
            const data = getDepartmentMutation.data
            departmentEditFormik.setValues({
                ...departmentEditFormik.values,
                ...data.data
            })
        }
    }, [getDepartmentMutation.data])

    useEffect(() => {
        if (getDepartmentMutation.error) {
            const message = getErrorText(getDepartmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [getDepartmentMutation.error])

    useEffect(() => {
        const data = editDepartmentMutation.data

        if (data) {
            if (data.status === 'OK') {
                // close component by clearing the edit formik.
                toast.success(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                // departmentEditFormik.resetForm()
                fetchData(token)
            } else {
                toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [editDepartmentMutation.data])

    useEffect(() => {
        if (editDepartmentMutation.error) {
            const message = getErrorText(editDepartmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [editDepartmentMutation.error])

    useEffect(() => {
        if (getUnlinkedCareprofessionalsMutation.error) {
            const message = getErrorText(getUnlinkedCareprofessionalsMutation.error)
            if (message.includes('Aborted') === false) {
                console.error(message)
                toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [getUnlinkedCareprofessionalsMutation.error])

    useEffect(() => {
        if (getLinkedCareprofessionalsMutation.error) {
            const message = getErrorText(getLinkedCareprofessionalsMutation.error)
            if (message.includes('Aborted') === false) {
                console.error(message)
                toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [getLinkedCareprofessionalsMutation.error])

    useEffect(() => {
        if (getUnlinkedTreatmentsMutation.error) {
            const message = getErrorText(getUnlinkedTreatmentsMutation.error)
            if (message.includes('Aborted') === false) {
                console.error(message)
                toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [getUnlinkedTreatmentsMutation.error])

    useEffect(() => {
        if (getLinkedTreatmentsMutation.error) {
            const message = getErrorText(getLinkedTreatmentsMutation.error)
            if (message.includes('Aborted') === false) {
                console.error(message)
                toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [getLinkedTreatmentsMutation.error])

    const changeCareprofessionalStatus = async (departmentId: string,
        obj: CareProfessional, linked: boolean) => {
        const newToken = await revalidateToken(
            {
                value: token.value,
                id: token.id
            },
            token.mode
        )

        if (linked) {
            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.addDepartmentCareProfessional.path,
                true
            )
            if (foundApiPath && newToken.value) {
                addDepartmentCareprofessional({
                    authToken: newToken.value,
                    data: {
                        departmentId: departmentId || '',
                        careProfessionalId: obj?.careProfessionalId || ''
                    }
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                        //
                        // fetchData(token)
                        fetchLinkedCareprofessionals(token)
                        fetchUnlinkedCareprofessionals(token)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        } else {
            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.deleteDepartmentCareProfessional.path,
                true
            )
            if (foundApiPath && newToken.value) {
                deleteDepartmentCareProfessional({
                    authToken: newToken.value,
                    data: {
                        departmentId: departmentId || '',
                        careProfessionalId: obj?.careProfessionalId || ''
                    }
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                        //
                        // fetchData(token)
                        fetchLinkedCareprofessionals(token)
                        fetchUnlinkedCareprofessionals(token)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        }
    }

    const changeTreatmentStatus = async (
        departmentId: string,
        obj: Treatment, linked: boolean
    ) => {
        const newToken = await revalidateToken(
            {
                value: token.value,
                id: token.id
            },
            token.mode
        )

        if (linked) {
            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.addDepartmentTreatment.path,
                true
            )
            if (foundApiPath && newToken.value) {
                addDepartmentTreatment({
                    authToken: newToken.value,
                    data: {
                        departmentId: departmentId || '',
                        treatmentId: obj?.treatmentId || ''
                    }
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                        //
                        fetchDataLinkedTreatments(token)
                        fetchDataUnlinkedTreatments(token)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        } else {
            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.deleteDepartmentTreatment.path,
                true
            )
            if (foundApiPath && newToken.value) {
                deleteDepartmentTreatment({
                    authToken: newToken.value,
                    data: {
                        departmentId: departmentId || '',
                        treatmentId: obj?.treatmentId || ''
                    }
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                        //
                        fetchDataLinkedTreatments(token)
                        fetchDataUnlinkedTreatments(token)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        }
    }

    const [updateProfilePictureDepartment, updateProfilePictureDepartmentMutation] =
    useUpdateProfilePictureDepartmentMutation()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            setFile(selectedFile)
        }
    }

    const handleUpload = async () => {
        if (file) {
            const formData = new FormData()
            formData.append('profilePicture', file)

            const newToken = await revalidateToken({
                value: token.value,
                id: token.id
            }, token.mode)

            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.updateProfilePictureDepartment.path,
                true
            )

            if (foundApiPath) {
                updateProfilePictureDepartment({
                    departmentId: departmentId || '',
                    data: formData,
                    authToken: newToken.value
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                        // toast success message.
                        toast.success(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                        // we need to use this token now so set it.
                        setShowRecentlyUploaded(true)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        }
    }

    useEffect(() => {
        handleUpload()
    }, [file])

    useEffect(() => {
        if (updateProfilePictureDepartmentMutation.error) {
            const message = getErrorText(updateProfilePictureDepartmentMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [updateProfilePictureDepartmentMutation.error])

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
        fieldName?: DepartmentKeys
    ) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            const nextInput = document.querySelector(`[name=${ fieldName }]`) as HTMLInputElement
            if (nextInput) {
                nextInput.focus()
                nextInput.select()
            } else {
                departmentEditFormik.handleSubmit()
            }
        }
    }

    const DepartmentNameInput = useMemo(() => {
        return <DepartmentName
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'departmentPostalcode')
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentName,
        departmentEditFormik.errors.departmentName
    ])

    const PostalCodeInput = useMemo(() => {
        return <PostalCode
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'departmentAddressNumber')
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentPostalcode,
        departmentEditFormik.errors.departmentPostalcode
    ])

    const AddressInput = useMemo(() => {
        return <Address
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={handleKeyDown}

        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentAddressNumber,
        departmentEditFormik.errors.departmentAddressNumber,
        departmentEditFormik.values.departmentAddressSuffix,
        departmentEditFormik.errors.departmentAddressSuffix
    ])

    const StreetNameInput = useMemo(() => {
        return <StreetName
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'departmentCity')
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentAddressStreet,
        departmentEditFormik.errors.departmentAddressStreet
    ])

    const CityInput = useMemo(() => {
        return <City
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'departmentPhonenumber')
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentCity,
        departmentEditFormik.errors.departmentCity
    ])

    /** GROUP 2 */

    const GeneralPhoneNumberInput = useMemo(() => {
        return <GeneralPhoneNumber
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'departmentEmailAddress')
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentPhonenumber,
        departmentEditFormik.errors.departmentPhonenumber
    ])

    const EmailInput = useMemo(() => {
        return <Email
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'departmentWebsite')
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentEmailAddress,
        departmentEditFormik.errors.departmentEmailAddress
    ])

    const WebsiteInput = useMemo(() => {
        return <Website
            readOnly={mode === 'VIEW'}
            departmentEditFormik={departmentEditFormik}
            handleKeyDown={(e) => {
                if (e.key === 'Enter') { departmentEditFormik.handleSubmit() }
            }}
        />
    }, [
        mode,
        strings,
        departmentEditFormik.values.departmentWebsite,
        departmentEditFormik.errors.departmentWebsite
    ])

    const CancelButton = <button type={'button'}
        className={[
            'btn btn-secondary w-100 py-2'
        ].join(' ')}
        onClick={() => {
            // if you made changes to the formik, show a warning first via popup
            const obj = departmentEditFormik.touched

            interface FormikTouched {
                [key: string]: boolean;
              }

            function hasTrueValue (obj: FormikTouched) {
                for (const key in obj) {
                    if (obj[key] === true) {
                        return true
                    }
                }
                return false
            }

            const hasTrue = hasTrueValue(obj)
            if (hasTrue) {
                // show warning pls
                setShowCancelWarning(true)
            } else {
                // go back to view
                const isValid = validateRoute(
                    activeModules.arr,
                    MODULE_TABLE.fmt.moduleName,
                    MODULE_TABLE.fmt.routes.viewDepartment,
                    true
                )

                setModeButtonClicked(true)

                dispatch(push(
                    _.replace(isValid.route,
                        ':departmentId',
                        departmentId || ''
                    )
                ))
            }
        }}>
        {strings.app?.text.cancel}
    </button>
    const LoadingContent = (
        <div className={'spinner-container'}>
            <span className={'spinner-border spinner-border-sm'}></span>
            <span className={'ms-2'}>{
                strings.app?.text.loading || ''
            }</span>
        </div>
    )
    const SubmitButton = <button type={'button'}
        disabled={
        // step 1 shouldn't be disabled here because of formik.
            false
        // step 2 can be skipped entirely.
        }
        className={[
            'btn btn-primary w-100 fw-semibold py-2'
        ].join(' ')}
        onClick={() => {
            departmentEditFormik.handleSubmit()
        }}>
        { editDepartmentMutation.isLoading
            ? LoadingContent
            : strings.app?.text.save}
    </button>

    const EditButton = <button type={'button'}
        className={[
            'btn btn-secondary w-100 py-2'
        ].join(' ')}
        onClick={() => {
            const isValid = validateRoute(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.routes.editDepartment,
                true
            )

            setModeButtonClicked(true)

            dispatch(push(
                _.replace(isValid.route,
                    ':departmentId',
                    departmentId || ''
                )
            ))
        }}>
        {strings.fmt?.text.department.details.edit}
    </button>

    const ChangeProfilePictureButton = <>
        <input
            type={'file'}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
        />
        <button type={'button'}
            className={[
                'btn btn-secondary w-100 mt-5'
            ].join(' ')}
            onClick={() => {
                if (fileInputRef.current) {
                    fileInputRef.current.click()
                }
            }}>
            { editDepartmentMutation.isLoading
                ? LoadingContent
                : strings.fmt?.text.department.details.change}
        </button>
    </>

    /** display list of departments here with the data table template. */

    const result = <div className={'row my-8'}>
        <div className={'col-12 col-lg'}>
            <div className={'edit-interface'}>
                <h2 className={'mb-5 fw-semibold '} >{strings.fmt
                    ?.text.facility.add.steps['2'].department_data}</h2>
                {/* group one */}
                <div className={'mb-6'}>
                    <h4 className={'mb-6'}>{strings.fmt?.text.facility.menu
                        .name_and_address_details}</h4>
                    {DepartmentNameInput}
                    {PostalCodeInput}
                    {AddressInput}
                    {StreetNameInput}
                    {CityInput}
                </div>
                {/* group two */}

                <div>
                    <h4 className={'mb-6'}>{strings.fmt?.text.facility.menu
                        .contact_details}</h4>
                    {GeneralPhoneNumberInput}
                    {EmailInput}
                    {WebsiteInput}
                </div>

                {
                    getDepartmentMutation.data?.data.departmentId
                        ? mode === 'EDIT'
                            ? <div>
                                <CareProfessionalsInterface
                                    department={getDepartmentMutation.data?.data}
                                    isFromEditPage={true}
                                    unlinkedCareprofessionals={
                                        getUnlinkedCareprofessionalsResponse}
                                    linkedCareprofessionals={
                                        getLinkedCareprofessionalsResponse}
                                    LinkCareprofessionalsFormik={LinkCareprofessionalsFormik}
                                    changeCareprofessionalStatus={changeCareprofessionalStatus}
                                    search={searchUnlinkedCareprof}
                                    setSearch={setSearchUnlinkedCareprof}
                                    currentPage={currentPageUnlinkedCareprof}
                                    setCurrentPage={setCurrentPageUnlinkedCareprof}
                                />
                                <TreatmentsInterface
                                    department={getDepartmentMutation.data?.data}
                                    isFromEditPage={true}
                                    unlinkedTreatments={
                                        getUnlinkedTreatmentsResponse}
                                    linkedTreatments={
                                        getLinkedTreatmentsResponse}
                                    LinkedTreatmentsFormik={LinkTreatmentsFormik}
                                    changeTreatmentStatus={changeTreatmentStatus}
                                    search={searchTreatment}
                                    setSearch={setSearchTreatment}
                                    currentPage={currentPageTreatment}
                                    setCurrentPage={setCurrentPageTreatment}
                                />
                            </div>
                            : <>
                                {/* view mode goes to tables instead */}
                            </>
                        : <></>
                }

                {mode === 'EDIT' && <div className={'container button-container'}>
                    <div className={'row align-items-center mt-6'}>
                        <div className={'col-6 col-lg-4 col-md-6 offset-lg-4 offset-md-0'}>
                            {CancelButton}
                        </div>
                        <div className={'col-6 col-lg-4 col-md-6'}>
                            {SubmitButton}
                        </div>
                    </div>
                </div>}

                {mode === 'VIEW' && <div className={'container button-container'}>
                    <div className={'align-items-center justify-content-end mt-6 row'}>
                        <div className={'col-6 col-lg-4 col-md-6'}>
                            {EditButton}
                        </div>
                    </div>
                </div>}
            </div>
        </div>
        <div className={'col-12 col-lg-auto mt-6 mt-lg-0'}>
            <div className={'picture-interface'}>
                <h2 className={'mb-5 fw-semibold '} >{strings.fmt?.text
                    .department.details.image}</h2>
                <div className={'text-center'}>

                    <div className={'img-wrapper mx-auto mb-2'}>

                        {<ProfilePic
                            showRecentlyUploaded={showRecentlyUploaded}
                            updateProfilePictureDepartmentMutation={{
                                data: updateProfilePictureDepartmentMutation.data
                            }}
                            getDepartmentMutation={getDepartmentMutation}
                        />}

                    </div>
                    {
                        mode === 'EDIT' && ChangeProfilePictureButton
                    }
                </div>
            </div>
        </div>

    </div>

    // _.map(
    //     linkedProfessionalsBySelectedId, (obj) => {
    //         return obj.careProfessionalId
    //     }
    // )

    const CareProfessionalLinkedTable = <CareProfessionalsViewTable
        mode={mode}
        search={searchLinkedCareprofs}
        setSearch={setSearchLinkedCareprofs}
        currentPage={currentPageCareprofessionals}
        setCurrentPage={setCurrentPageCareprofessionals}
        getCareprofessionalsMutation={{
            responseData: getLinkedCareprofessionalsResponse,
            isLoading: getLinkedCareprofessionalsMutation.isLoading,
            isSuccess: getLinkedCareprofessionalsMutation.isSuccess
        }}
        fetchData={fetchUnlinkedCareprofessionals}
    />

    const TreatmentLinkedTable = <TreatmentsTable
        mode={mode}
        search={searchLinkedTreatments}
        setSearch={setSearchLinkedTreatments}
        currentPage={currentPageTreatments}
        setCurrentPage={setCurrentPageTreatments}
        responseData={getLinkedTreatmentsMutation.data}
        fetchData={fetchDataUnlinkedTreatments}
    />

    return <div className={'department-page'}>

        {/* dashboard responsive header */}
        <DashboardResponsiveHeader />

        <div className={'main-content'}>

            <div className={'container-fluid mx-auto'}>
                <div className={'row justify-content-between pt-16 pb-0 px-5'}>
                    <div className={'col-auto'}>
                        <a onClick={(e) => {
                            e.preventDefault()
                            dispatch(back())
                        }} className={'btn btn-round btn-sm-md'}>
                            <i
                                className={'fa-light fa-arrow-left'}
                                aria-hidden={'true'}>
                            </i>
                        </a>
                    </div>
                    <div className={'col-auto'}>
                        <h1 className={'my-0'}>{
                            getDepartmentMutation.data
                                ?.data.departmentName
                        }</h1>
                    </div>
                    <div className={'col-auto'}>
                        <a href={'#'}
                            onClick={(e) => {
                                e.preventDefault()
                            }} className={[
                                'btn btn-round btn-sm-md invisible'
                            ].join(' ')}>
                            <i
                                className={'fa-light fa-arrow-right'}
                                aria-hidden={'true'}
                            >
                            </i>
                        </a>
                    </div>
                </div>
                <div className={'row justify-content-between mt-5'}>
                    <div className={'col'}>
                        <div className={
                            'container-fluid px-5 px-sm-3 department-body'
                        }>
                            <div className={'h-100 row'}>
                                <div className={' col-12 mx-auto'}>
                                    {result}
                                </div>
                            </div>
                        </div>

                        <div className={
                            'container-fluid px-5 px-sm-3 department-body list-interface my-4'
                        }>
                            <div className={'h-100 row'}>
                                <div className={' col-12 mx-auto'}>
                                    <h5 className={'fw-semibold'}>{strings.fmt
                                        ?.text.facility.add.careprofessionals}</h5>
                                    {CareProfessionalLinkedTable}
                                </div>
                            </div>
                        </div>

                        <div className={
                            'container-fluid px-5 px-sm-3 department-body list-interface my-4'
                        }>
                            <div className={'h-100 row'}>
                                <div className={' col-12 mx-auto'}>
                                    <h5 className={'fw-semibold'}>{strings.fmt
                                        ?.text.facility.add.treatment}</h5>
                                    {TreatmentLinkedTable}
                                </div>
                            </div>
                        </div>

                        <Modal centered={true} isOpen={showCancelWarning} toggle={() => {
                            setShowCancelWarning(!showCancelWarning)
                        }}>
                            <ModalHeader className={'justify-content-between'} toggle={() => {
                                setShowCancelWarning(!showCancelWarning)
                            }} close={ <a className={'btn btn-round '}
                                onClick={(e) => {
                                    e.preventDefault()
                                    setShowCancelWarning(false)
                                }}>
                                <i className={'fa-light fa-multiply'} aria-hidden={'true'}>
                                </i>
                            </a>}
                            >
                                <span className={'h5'}>{
                                    strings.fmt?.message?.data_edit_warning?.title || ''
                                }</span>
                            </ModalHeader>
                            <ModalBody>
                                {/* content changes if idle / expire sessions expired. */}
                                <small className={'d-block my-4'}>
                                    {strings.fmt?.message?.data_edit_warning?.content || ''}
                                </small>
                                <div className={'row justify-content-end'}>
                                    <div className={'col-auto mb-2 mb-md-0'}>
                                        <button
                                            type={'button'}
                                            className={'btn btn-primary w-100'}
                                            onClick={ () => {
                                                setShowCancelWarning(false)
                                            }}>
                                            {strings.app?.text?.no || ''}
                                        </button>
                                    </div>
                                    <div className={'col-auto'}>
                                        <button type={'button'} onClick={() => {
                                            const isValid = validateRoute(
                                                activeModules.arr,
                                                MODULE_TABLE.fmt.moduleName,
                                                MODULE_TABLE.fmt
                                                    .routes.viewDepartment,
                                                true
                                            )

                                            setModeButtonClicked(true)

                                            dispatch(push(
                                                _.replace(isValid.route,
                                                    ':departmentId',
                                                    departmentId || ''
                                                )
                                            ))

                                            setShowCancelWarning(false)
                                        }} className={'btn btn-primary w-100'}>
                                            {strings.app?.text.yes}
                                        </button>

                                    </div>
                                </div>
                            </ModalBody>
                        </Modal>

                    </div>
                </div>

            </div>

        </div>

        <div
            className={'position-fixed bottom-0 end-0 pe-5 fs-label fw-light version-text'}
        >
            {MODULE_VERSION}
        </div>
    </div>
}

export default DepartmentDetailsInterface
